// Explanatory engineering systems-diagram: many small inputs -> cumulative burden.
// CSS/SMIL-driven for a reliable final state; teaches the concept within a few seconds.
const W = 156, H = 44;
const CORE = { x: 300, y: 180 };
const NODES = [
  { id: "decision-systems", label: "Decision Systems", code: "01", bx: 24, by: 64, side: "left", color: "#00E599" },
  { id: "home-systems", label: "Home Systems", code: "02", bx: 420, by: 64, side: "right", color: "#FFB259" },
  { id: "body-inputs", label: "Body Inputs", code: "03", bx: 24, by: 252, side: "left", color: "#43C6F0" },
  { id: "beyond-the-home", label: "Beyond the Home", code: "04", bx: 420, by: 252, side: "right", color: "#B98CFF" },
].map((n) => ({ ...n, cx: n.side === "left" ? n.bx + W : n.bx, cy: n.by + H / 2 }));

// Small "input source" dots feeding each domain from its outer edge (many small inputs).
function sourcesFor(n) {
  const dir = n.side === "left" ? -1 : 1;
  const edgeX = n.side === "left" ? n.bx : n.bx + W;
  const sx = edgeX + dir * 30;
  return [n.by + 10, n.by + 22, n.by + 34].map((sy, k) => ({ sx, sy, edgeX, k }));
}

export default function SystemsDiagramHero() {
  return (
    <div className="relative w-full select-none" data-testid="systems-diagram">
      <svg viewBox="0 0 600 372" className="w-full h-auto" role="img" aria-label="Diagram: many small exposure inputs accumulate into a single cumulative burden">
        <defs>
          <radialGradient id="coreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#00E599" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#00E599" stopOpacity="0" />
          </radialGradient>
          {NODES.map((n) => (
            <marker key={`m-${n.id}`} id={`arrow-${n.id}`} markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
              <path d="M0,0 L6,3.5 L0,7 Z" fill={n.color} opacity="0.8" />
            </marker>
          ))}
        </defs>

        {/* Input sources -> domain nodes (many small inputs) */}
        {NODES.map((n) => (
          <g key={`src-${n.id}`}>
            {sourcesFor(n).map((s) => (
              <g key={`s-${n.id}-${s.k}`}>
                <line x1={s.sx} y1={s.sy} x2={s.edgeX} y2={s.sy} stroke={n.color} strokeWidth="0.9" opacity="0.35" />
                <circle cx={s.sx} cy={s.sy} r="1.8" fill={n.color}>
                  <animate attributeName="opacity" values="0.25;1;0.25" dur="2.2s" begin={`${s.k * 0.35}s`} repeatCount="indefinite" />
                </circle>
                {/* micro input travelling into the domain node */}
                <circle r="1.4" fill={n.color} opacity="0">
                  <animate attributeName="cx" values={`${s.sx};${s.edgeX}`} dur="1.6s" begin={`${0.3 + s.k * 0.45}s`} repeatCount="indefinite" />
                  <animate attributeName="cy" values={`${s.sy};${s.sy}`} dur="1.6s" begin={`${0.3 + s.k * 0.45}s`} repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0;1;1;0" dur="1.6s" begin={`${0.3 + s.k * 0.45}s`} repeatCount="indefinite" />
                </circle>
              </g>
            ))}
          </g>
        ))}

        {/* Domain -> core edges with accumulating particles */}
        {NODES.map((n, i) => {
          const midX = (n.cx + CORE.x) / 2;
          const d = `M ${n.cx} ${n.cy} C ${midX} ${n.cy}, ${midX} ${CORE.y}, ${CORE.x} ${CORE.y}`;
          return (
            <g key={`edge-${n.id}`}>
              <path d={d} fill="none" stroke={n.color} strokeWidth="1.25" strokeDasharray="4 4" opacity="0.5" markerEnd={`url(#arrow-${n.id})`} />
              {/* two staggered particles per edge to convey continuous accumulation */}
              <circle r="2.8" fill={n.color}>
                <animateMotion dur="2.6s" begin={`${1 + i * 0.3}s`} repeatCount="indefinite" path={d} />
                <animate attributeName="opacity" values="0;1;1;0" dur="2.6s" begin={`${1 + i * 0.3}s`} repeatCount="indefinite" />
              </circle>
              <circle r="2.2" fill={n.color}>
                <animateMotion dur="2.6s" begin={`${2.3 + i * 0.3}s`} repeatCount="indefinite" path={d} />
                <animate attributeName="opacity" values="0;1;1;0" dur="2.6s" begin={`${2.3 + i * 0.3}s`} repeatCount="indefinite" />
              </circle>
            </g>
          );
        })}

        {/* Core: cumulative burden, pulses as inputs arrive */}
        <circle cx={CORE.x} cy={CORE.y} r="64" fill="url(#coreGlow)" />
        <circle cx={CORE.x} cy={CORE.y} r="36" fill="#0C100C" stroke="#00E599" strokeWidth="1.5" className="core-pulse">
          <animate attributeName="r" values="35;38;35" dur="2.6s" repeatCount="indefinite" />
        </circle>
        <text x={CORE.x} y={CORE.y - 4} textAnchor="middle" fill="#00E599" style={{ fontFamily: "JetBrains Mono", fontSize: 7.5, letterSpacing: 1 }}>CUMULATIVE</text>
        <text x={CORE.x} y={CORE.y + 9} textAnchor="middle" fill="#ffffff" style={{ fontFamily: "Chivo", fontWeight: 700, fontSize: 12 }}>BURDEN</text>

        {/* Domain nodes */}
        {NODES.map((n, i) => (
          <g key={`node-${n.id}`} className="diagram-node" style={{ animationDelay: `${0.2 + i * 0.18}s` }}>
            <rect x={n.bx} y={n.by} width={W} height={H} fill="#0C100C" stroke={n.color} strokeOpacity="0.55" strokeWidth="1" />
            <line x1={n.bx} y1={n.by} x2={n.bx + 8} y2={n.by} stroke={n.color} strokeWidth="1.5" />
            <line x1={n.bx} y1={n.by} x2={n.bx} y2={n.by + 8} stroke={n.color} strokeWidth="1.5" />
            <line x1={n.bx + W} y1={n.by + H} x2={n.bx + W - 8} y2={n.by + H} stroke={n.color} strokeWidth="1.5" />
            <line x1={n.bx + W} y1={n.by + H} x2={n.bx + W} y2={n.by + H - 8} stroke={n.color} strokeWidth="1.5" />
            <text x={n.bx + 14} y={n.by + 17} fill={n.color} style={{ fontFamily: "JetBrains Mono", fontSize: 7, letterSpacing: 1 }}>{`[ ${n.code} ]`}</text>
            <text x={n.bx + 14} y={n.by + 32} fill="#ffffff" style={{ fontFamily: "Chivo", fontWeight: 500, fontSize: 12 }}>{n.label}</text>
          </g>
        ))}
      </svg>
      <p data-testid="diagram-caption" className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.22em] text-foreground/45">
        Many small inputs accumulate into one cumulative load
      </p>
    </div>
  );
}
